import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { pathScreens } from "../../../shared/tour";
import { setMonitoringTags } from "../../monitoring";
import { useEditor } from "../DemoEditor";
import { modKey } from "../shell/TopBar";
import { MEDIA_ACCEPT, useStepActions } from "./useStepActions";
import StepCanvas, { type CanvasHandle, type Mode } from "./StepCanvas";
import StepList from "./StepList";
import StepStrip from "./StepStrip";
import { DRAWER_ID, useCompact, type Drawer } from "./compact";
import { StepActionsBar, StepPanel } from "./StepPanel";
import { usePathParam } from "./paths";
import { getSaveStatus, subscribeSaveStatus, type SaveStatus } from "../dirty";

/**
 * The editing workspace for one demo: the tour's screens on the left, the selected screen on a
 * canvas in the middle, and an inspector on the right. It replaces the old Screens and Hotspots
 * steps. Switching screens or paths saves the screen you're leaving first.
 *
 * In a window under 1280px wide (`useCompact`) the screens are a strip of thumbnails, and the full list and the
 * inspector are drawers that open over the canvas when asked for. They overlay rather than push, so opening one
 * never resizes the canvas, and both stay mounted while closed, so nothing in them (a pending save, a half-typed
 * caption) is lost by closing them.
 */
export default function Studio() {
  const { bundle, setBundle, setDirty, go } = useEditor();
  const [pathId, setPathId] = usePathParam();
  const [params, setParams] = useSearchParams();
  // Only this path's own screens: a screen that is on other paths only is not listed.
  const seq = useMemo(() => pathScreens(bundle, pathId), [bundle, pathId]);
  const wanted = params.get("step");
  const stepId = wanted && seq.some((s) => s.id === wanted) ? wanted : seq[0]?.id;
  const index = seq.findIndex((s) => s.id === stepId);
  const step = index >= 0 ? seq[index] : undefined;

  // Error reports from the Studio say which screen (and path) was open.
  useEffect(() => {
    setMonitoringTags({ screen_id: stepId, path_id: pathId });
    return () => setMonitoringTags({ screen_id: null, path_id: null });
  }, [stepId, pathId]);

  const canvas = useRef<CanvasHandle>(null);
  const [mode, setMode] = useState<Mode>("hotspots");
  const [nonce, setNonce] = useState(0); // bumped to throw away unsaved edits
  const [unsaved, setUnsavedHere] = useState(false);
  const [saveStatus, setLocalSaveStatus] = useState<SaveStatus>(getSaveStatus());
  useEffect(() => subscribeSaveStatus((s) => setLocalSaveStatus(s)), []);
  const onDirtyChange = useCallback((d: boolean) => { setDirty(d); setUnsavedHere(d); }, [setDirty]);

  const compact = useCompact();
  const [drawer, setDrawer] = useState<Drawer>(null);
  useEffect(() => { if (!compact) setDrawer(null); }, [compact]);
  const toggleDrawer = useCallback((d: Exclude<Drawer, null>) => setDrawer((cur) => (cur === d ? null : d)), []);
  // Escape closes a drawer while you're working in it (focus inside it, or on the button that opened it), and then
  // only the drawer: it doesn't also deselect a hotspot. Anywhere else Escape does what it always did. A field you're
  // typing in, or a dialog on top (Quick Preview, the command menu), keeps Escape for itself.
  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const t = e.target as HTMLElement;
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName) || t.isContentEditable;
      const id = DRAWER_ID[drawer];
      const here = t.closest?.(`#${id}, [aria-controls="${id}"]`);
      if (!here || typing || document.querySelector('[aria-modal="true"]')) return;
      e.stopPropagation();
      setDrawer(null);
      document.querySelector<HTMLElement>(`[aria-controls="${id}"]`)?.focus();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [drawer]);

  const setStepParam = useCallback((id: string) => {
    setParams((p) => { const n = new URLSearchParams(p); n.set("step", id); return n; }, { replace: true });
  }, [setParams]);

  /** Saves the open screen if it has changes. False means it failed and we should stay put. */
  const settle = async () => !canvas.current?.isDirty() || (await canvas.current.save());
  const select = async (id: string) => { if (id !== stepId && (await settle())) setStepParam(id); };
  const changePath = async (v: string | null) => { if (await settle()) setPathId(v); };

  const actions = useStepActions(pathId, seq, async (id) => { await settle(); setStepParam(id); });

  const kind = step
    ? step.textCard
      ? step.textCard.role === "intro"
        ? "Intro screen"
        : step.textCard.role === "outro"
          ? "Outro screen"
          : "Text card"
      : step.captureKey
        ? "HTML capture"
        : step.mediaType === "video"
          ? "Video"
          : "Screenshot"
    : "";

  return (
    <div className={`studio-wrap ${compact ? "compact" : ""}`}>
      <div className="studio-strip" role="status">
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          onClick={async () => { if (await settle()) go("outcomes"); }}
          title="Back to Paths"
        >
          ← Paths
        </button>
        <span className="strip-slash" aria-hidden>/</span>
        <span className={`strip-on ${saveStatus === "dirty" || unsaved ? "strip-dirty" : ""}`}>{step ? `Screen ${index + 1} of ${seq.length}` : "No screens yet"}</span>
        {step && (
          <>
            <span className="strip-slash">/</span>
            <span className="strip-title">{step.caption || "Untitled screen"}</span>
            <span className="strip-chip">{kind}</span>
            {!step.textCard && <span>{step.imageWidth} × {step.imageHeight}</span>}
          </>
        )}
        <span className="spacer" />
        {step && saveStatus === "error" && (
          <span className="strip-status strip-error">Save failed (retry)</span>
        )}
        <span className="strip-keys" aria-hidden><kbd>{modKey} Z</kbd><kbd>{modKey} ⇧ Z</kbd><kbd>{modKey} S</kbd></span>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          style={{ marginLeft: 8 }}
          onClick={async () => {
            if (await settle()) go("survey");
          }}
        >
          Continue to Survey →
        </button>
      </div>
    <div className="studio">
      {compact && <StepStrip seq={seq} stepId={stepId} onSelect={(id) => void select(id)} listOpen={drawer === "steps"} onToggleList={() => toggleDrawer("steps")} />}
      <StepList seq={seq} pathId={pathId} onPath={changePath} stepId={stepId} actions={actions}
        // Picking a screen from the drawer shows it: the drawer gets out of the way.
        onSelect={(id) => { void select(id); if (compact) setDrawer(null); }}
        drawer={compact ? { open: drawer === "steps", onClose: () => setDrawer(null) } : undefined} />
      {step ? (
        <StepCanvas
          key={`${step.id}:${step.imageKey}:${nonce}`}
          ref={canvas}
          step={step}
          index={index}
          total={seq.length}
          pathId={pathId}
          mode={mode}
          onModeChange={setMode}
          onSaved={setBundle}
          onDiscard={() => setNonce((n) => n + 1)}
          onDirtyChange={onDirtyChange}
          compact={compact}
          inspectorOpen={drawer === "inspector"}
          onToggleInspector={() => toggleDrawer("inspector")}
          stepPanel={<StepPanel step={step} />}
          stepActions={<StepActionsBar step={step} actions={actions} />}
        />
      ) : (
        <>
          <section className="studio-canvas">
            <label className="studio-drop">
              <b>Drop screenshots here, or click to choose</b>
              <span className="small muted">Several at once is fine. They're added in filename order.</span>
              <input type="file" multiple hidden accept={MEDIA_ACCEPT} onChange={(e) => { void actions.upload(e.target.files); e.target.value = ""; }} />
            </label>
          </section>
          {!compact && (
            <aside className="studio-inspector" aria-label="Inspector">
              <div className="insp-body"><div className="panel stack-s"><h3>Inspector</h3><p className="small muted">Add a screen to start. Then draw hotspots on it and set what each one does.</p></div></div>
            </aside>
          )}
        </>
      )}
    </div>
    </div>
  );
}
